<template>
  <div>
    <div class="units-heading">
      <div>
        <h2 class="title is-4">Unidades de compra</h2>
        <p class="subtitle is-6">
          {{
            pncpWriteEnabled
              ? 'Organize as unidades de compra do seu órgão.'
              : 'Visualize as unidades de compra do seu órgão.'
          }}
        </p>
      </div>
      <div class="units-actions">
        <b-button
          v-if="canSyncUnits"
          type="is-primary"
          :loading="syncing"
          :disabled="syncing"
          @click="syncUnits"
        >
          Sincronizar unidades
        </b-button>
        <b-button v-if="canManageUnits" type="is-primary" @click="openCreate">
          Nova unidade
        </b-button>
      </div>
    </div>

    <b-notification v-if="error" type="is-danger" has-icon :closable="false">
      Não foi possível carregar as unidades. Tente novamente.
      <b-button type="is-danger" size="is-small" outlined @click="loadUnits"
        >Tentar novamente</b-button
      >
    </b-notification>

    <b-notification v-if="!pncpWriteEnabled" type="is-info" has-icon :closable="false">
      As unidades de compra são cadastradas no PNCP e chegam a esta tela pela sincronização e
      leitura do PNCP. Por isso, não podem ser cadastradas aqui.
    </b-notification>

    <b-loading :active="loading" :is-full-page="false" />

    <div class="table-wrap">
      <b-table class="units-table" :data="units" :mobile-cards="true" striped hoverable>
        <b-table-column
          class="units-code-column"
          field="code"
          label="Código"
          sortable
          v-slot="props"
          >{{ props.row.code }}</b-table-column
        >
        <b-table-column
          class="units-name-column"
          field="name"
          label="Nome"
          sortable
          v-slot="props"
          >{{ props.row.name }}</b-table-column
        >
        <b-table-column class="units-status-column" label="Situação" v-slot="props">
          <b-tag :type="props.row.status === 'ACTIVE' ? 'is-success' : 'is-light'">
            {{ operationalLabel(props.row.status) }}
          </b-tag>
        </b-table-column>
        <b-table-column class="units-sync-column" label="PNCP" v-slot="props">
          <span :class="syncClass(props.row.syncStatus)">{{
            syncLabel(props.row.syncStatus)
          }}</span>
        </b-table-column>
        <b-table-column
          v-if="isSelcorpAdmin && pncpWriteEnabled"
          class="units-actions-column"
          label="Ações"
          centered
          v-slot="props"
        >
          <b-button type="is-primary" size="is-small" outlined @click="openEdit(props.row)">
            Editar
          </b-button>
        </b-table-column>
        <template #empty>
          <div class="empty-state">
            <b-icon icon="domain" size="is-large" type="is-primary" />
            <h3 class="title is-5">Nenhuma unidade encontrada</h3>
            <p>Ainda não há unidades de compra cadastradas.</p>
            <b-button v-if="canManageUnits" class="mt-4" type="is-primary" @click="openCreate">
              Cadastrar primeira unidade
            </b-button>
          </div>
        </template>
      </b-table>
      <AppPagination
        v-if="paginationTotal"
        :data-filters="paginationFilters"
        :total="paginationTotal"
        @change-page="changePage"
        @change-per-page="changePerPage"
      />
    </div>

    <b-modal :active.sync="formOpen" has-modal-card :can-cancel="['escape', 'x', 'outside']">
      <form class="modal-card" @submit.prevent="saveUnit">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ editing ? 'Editar unidade' : 'Nova unidade' }}</p>
        </header>
        <section class="modal-card-body">
          <b-field
            label="Código"
            required
            :type="fieldError('code') ? 'is-danger' : ''"
            :message="fieldError('code')"
          >
            <b-input ref="code" v-model.trim="form.code" maxlength="80" :disabled="saving" />
          </b-field>
          <b-field
            label="Nome"
            required
            :type="fieldError('name') ? 'is-danger' : ''"
            :message="fieldError('name')"
          >
            <b-input v-model.trim="form.name" maxlength="160" :disabled="saving" />
          </b-field>
          <b-field v-if="editing" label="Situação operacional">
            <b-select v-model="form.status" expanded :disabled="saving">
              <option value="ACTIVE">Ativa</option>
              <option value="INACTIVE">Inativa</option>
            </b-select>
          </b-field>
          <p class="help">
            A unidade nunca é excluída. Ao inativá-la, ela deixa de ser uma opção operacional, mas
            continua no histórico.
          </p>
        </section>
        <footer class="modal-card-foot is-justify-content-flex-end">
          <b-button :disabled="saving" @click="formOpen = false">Cancelar</b-button>
          <b-button native-type="submit" type="is-primary" :loading="saving">Salvar</b-button>
        </footer>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import AppPagination from '@/components/AppPagination';
import { isSelcorpOperatorCompany } from '@/utils/postAuthRoute';

export default {
  name: 'BuyerUnits',
  components: { AppPagination },
  data() {
    return {
      page: 1,
      perPage: 20,
      syncing: false,
      formOpen: false,
      editing: null,
      saving: false,
      form: { code: '', name: '', status: 'ACTIVE' },
      submitted: false,
    };
  },
  computed: {
    ...mapState('companies', ['company']),
    ...mapGetters('purchasingUnits', {
      units: 'purchasingUnits',
      pagination: 'purchasingUnitsPagination',
      loading: 'purchasingUnitsLoading',
      error: 'purchasingUnitsError',
    }),
    ...mapGetters('companiesConfigurations', {
      configurations: 'getConfigurations',
    }),
    buyerId() {
      return Number(this.$route.params.id);
    },
    isSelcorpAdmin() {
      return isSelcorpOperatorCompany(this.company);
    },
    pncpWriteEnabled() {
      return this.configurations?.integratePncp !== false;
    },
    canManageUnits() {
      return (this.company.buyer || this.isSelcorpAdmin) && this.pncpWriteEnabled;
    },
    canSyncUnits() {
      return this.company.buyer || this.isSelcorpAdmin;
    },
    paginationTotal() {
      return Number(this.pagination.totalElements || 0);
    },
    paginationFilters() {
      return { page: this.page, perPage: this.perPage };
    },
  },
  methods: {
    ...mapActions('purchasingUnits', [
      'fetchPurchasingUnits',
      'createPurchasingUnit',
      'syncPurchasingUnits',
      'updatePurchasingUnit',
    ]),
    ...mapActions('companiesConfigurations', ['searchConfigurations']),
    params() {
      return {
        pageNumber: this.page,
        pageSize: this.perPage,
      };
    },
    async loadUnits() {
      try {
        await this.fetchPurchasingUnits({ buyerId: this.buyerId, params: this.params() });
      } catch (error) {
        /* state drives error */
      }
    },
    async loadConfigurations() {
      try {
        await this.searchConfigurations({ companyId: this.buyerId });
      } catch (error) {
        /* preserve the default write-enabled behavior */
      }
    },
    async syncUnits() {
      this.syncing = true;
      try {
        await this.syncPurchasingUnits({ buyerId: this.buyerId });
        this.$alerta('Unidades sincronizadas com sucesso', 'is-success');
      } catch (error) {
        this.$alerta(
          this.serverMessage(error) || 'Não foi possível sincronizar as unidades.',
          'is-danger',
        );
      } finally {
        await this.loadUnits();
        this.syncing = false;
      }
    },
    changePage(page) {
      this.page = page;
      this.loadUnits();
    },
    changePerPage(perPage) {
      this.perPage = Number(perPage);
      this.page = 1;
      this.loadUnits();
    },
    openCreate() {
      this.editing = null;
      this.form = { code: '', name: '', status: 'ACTIVE' };
      this.submitted = false;
      this.formOpen = true;
      this.$nextTick(() => this.$refs.code && this.$refs.code.focus());
    },
    openEdit(unit) {
      this.editing = unit;
      this.form = { code: unit.code || '', name: unit.name || '', status: unit.status || 'ACTIVE' };
      this.submitted = false;
      this.formOpen = true;
    },
    fieldError(field) {
      return this.submitted && !this.form[field]
        ? `${field === 'code' ? 'Código' : 'Nome'} é obrigatório`
        : '';
    },
    serverMessage(error) {
      return error?.response?.data?.details?.message || error?.response?.data?.message;
    },
    async saveUnit() {
      this.submitted = true;
      if (!this.form.code || !this.form.name) return;
      this.saving = true;
      try {
        const data = { code: this.form.code, name: this.form.name };
        if (this.editing) data.status = this.form.status;
        const action = this.editing ? this.updatePurchasingUnit : this.createPurchasingUnit;
        await action({
          buyerId: this.buyerId,
          unitId: this.editing && this.editing.id,
          data,
          params: this.params(),
        });
        this.formOpen = false;
        this.$alerta(`Unidade ${this.editing ? 'atualizada' : 'criada'}`, 'is-success');
      } catch (error) {
        this.$alerta(
          this.serverMessage(error) || `Erro ao ${this.editing ? 'atualizar' : 'criar'} unidade`,
          'is-danger',
        );
      } finally {
        this.saving = false;
      }
    },
    operationalLabel(status) {
      return status === 'ACTIVE' ? 'Ativa' : 'Inativa';
    },
    syncLabel(status) {
      return (
        {
          PENDENTE: 'Pendente',
          PROCESSANDO: 'Processando',
          INTEGRADO: 'Integrado',
          ERRO: 'Erro',
          CANCELADO: 'Cancelado',
        }[status] ||
        status ||
        'Não informado'
      );
    },
    syncClass(status) {
      return status === 'ERRO' ? 'has-text-danger has-text-weight-semibold' : '';
    },
  },
  created() {
    this.loadConfigurations();
    this.loadUnits();
  },
};
</script>

<style lang="scss" scoped>
.units-heading {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  gap: 1rem;
}
.units-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.empty-state {
  padding: 3rem 1.5rem;
  text-align: center;
}
.table-wrap {
  overflow-x: visible;
}
.units-table {
  width: 100%;

  ::v-deep table {
    table-layout: fixed;
    width: 100%;
  }

  ::v-deep .units-code-column {
    width: 8rem;
  }

  ::v-deep .units-name-column {
    width: auto;
  }

  ::v-deep .units-status-column {
    width: 7rem;
  }

  ::v-deep .units-sync-column {
    width: 8.5rem;
  }

  ::v-deep .units-actions-column {
    width: 7rem;
  }
}
@media (max-width: 768px) {
  .units-heading {
    align-items: stretch;
    flex-direction: column;
  }
  .units-actions {
    flex-direction: column;
  }
  .units-actions .button {
    width: 100%;
  }
}
@media (max-width: 640px) {
  .table-wrap {
    overflow-x: auto;
  }
  .units-table {
    min-width: 38rem;
  }
}
</style>
